import React, { useState } from 'react';
import {
    Box,
    Typography,
    useTheme,
    TextField,
    Button,
    MenuItem,
    Divider,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import { tokens } from '../../../../../theme';
import { CloudUploadOutlined, DownloadOutlined, DeleteOutlined } from '@mui/icons-material';

import {
    useUploadATGReportsMutation,
    useGetATGUploadedDocsQuery,
    useDeleteATGReportMutation
} from '../../../../../features/atgs/atgsApiSlice';

const docTypes = [
    'Delivery Certificate',
    'Maintenance Log',
    'Compliance Report',
    'Station General',
];

const StationUploadedDocs = ({ atg_name, atg_organization_id }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [newUploads, setNewUploads] = useState([]);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [docToDelete, setDocToDelete] = useState(null);

    const [uploadATGReports, { isLoading: isUploading, isSuccess, error }] = useUploadATGReportsMutation();
    const [deleteATGReport, { isLoading: isDeleting, error: deleteError }] = useDeleteATGReportMutation();

    const {
        data: uploadedDocs = [],
        isLoading,
        refetch
    } = useGetATGUploadedDocsQuery({
        atg_name,
        org_id: atg_organization_id
    });

    const handleFileDrop = (e) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files);
        const uploads = files.map((file) => ({
            file,
            name: file.name,
            type: 'Station General',
        }));
        setNewUploads((prev) => [...prev, ...uploads]);
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        const uploads = files.map((file) => ({
            file,
            name: file.name,
            type: 'Station General',
        }));
        setNewUploads((prev) => [...prev, ...uploads]);
    };

    const handleUploadConfirm = async () => {
        const formData = new FormData();
        formData.append('org_id', atg_organization_id);

        newUploads.forEach(upload => {
            formData.append('files', upload.file);
            formData.append(`name_${upload.file.name}`, upload.name);
            formData.append(`type_${upload.file.name}`, upload.type);
        });

        try {
            await uploadATGReports({ atg_name, formData }).unwrap();
            setNewUploads([]);
            refetch(); // reload document list after upload
        } catch (err) {
            console.error('Upload failed:', err);
        }
    };

    const handleDragOver = (e) => e.preventDefault();

    // Open the delete confirmation dialog and set the document to be deleted
    const handleDeleteClick = (doc) => {
        setDocToDelete(doc);
        setOpenDeleteDialog(true);
    };

    // Call the delete endpoint with proper parameters.
    // We use a fallback for s3Key if the key is nested under _doc.
    const handleDeleteConfirm = async () => {
        if (!docToDelete) return;
        const s3Key = docToDelete.s3Key || docToDelete._doc?.s3Key;
        const payload = {
            atg_name,
            org_id: atg_organization_id,
            s3Key
        };
        console.log('Deleting with payload:', payload); // Debug log

        try {
            await deleteATGReport(payload).unwrap();
            setOpenDeleteDialog(false);
            // Refetch the docs list to update the UI
            refetch();
        } catch (err) {
            console.error('Delete failed:', err);
            setOpenDeleteDialog(false);
        }
    };

    console.log('Uploaded Docs:', uploadedDocs);

    return (
        <>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={4}>
                {/* Left: Uploaded docs */}
                <Paper sx={{ flex: 1, p: 2 }}>
                    <Typography variant="h4" mb={2} color={colors.primary[100]}>
                        Uploaded Reports
                    </Typography>

                    {isLoading ? (
                        <Typography>Loading documents...</Typography>
                    ) : uploadedDocs.length === 0 ? (
                        <Typography>No uploaded reports yet.</Typography>
                    ) : (
                        uploadedDocs.map((doc, idx) => (
                            <Box
                                key={doc.s3Key || idx}
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={2}
                            >
                                <Box>
                                    <Typography sx={{ color: colors.greenAccent[100] }}>
                                        {doc.fileName || doc._doc?.fileName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {doc.type || doc._doc?.type}
                                    </Typography>
                                </Box>
                                <Box display="flex" gap={1}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        href={doc.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        startIcon={<DownloadOutlined />}
                                        sx={{
                                            color: colors.primary[200],
                                            borderColor: colors.primary[200],
                                            '&:hover': {
                                                backgroundColor: colors.primary[700],
                                                borderColor: colors.primary[300],
                                                color: colors.primary[100],
                                            },
                                        }}
                                    >
                                        View / Download
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        color="error"
                                        onClick={() => handleDeleteClick(doc)}
                                        startIcon={<DeleteOutlined />}
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: colors.redAccent[500],
                                                color: theme.palette.getContrastText(colors.redAccent[500]),
                                            },
                                        }}
                                    >
                                        Delete
                                    </Button>
                                </Box>
                            </Box>
                        ))
                    )}
                </Paper>

                {/* Right: Upload UI */}
                <Paper sx={{ flex: 1, p: 2 }}>
                    <Typography variant="h4" mb={2} color={colors.primary[100]}>
                        Upload New Document(s)
                    </Typography>

                    <Box
                        onDrop={handleFileDrop}
                        onDragOver={handleDragOver}
                        sx={{
                            border: `2px dashed ${colors.primary[300]}`,
                            borderRadius: '8px',
                            padding: 4,
                            textAlign: 'center',
                            mb: 3,
                            backgroundColor: colors.primary[800],
                        }}
                    >
                        <CloudUploadOutlined sx={{ fontSize: 40, mb: 1 }} />
                        <Typography>Drag and drop files here or</Typography>
                        <Button
                            variant="outlined"
                            component="label"
                            sx={{
                                mt: 1,
                                color: colors.primary[100],
                                borderColor: colors.primary[200],
                                '&:hover': {
                                    backgroundColor: colors.primary[600],
                                    color: theme.palette.getContrastText(colors.primary[400]),
                                    borderColor: colors.primary[400],
                                },
                            }}
                        >
                            Browse
                            <input type="file" multiple hidden onChange={handleFileChange} />
                        </Button>
                    </Box>

                    {newUploads.length > 0 && (
                        <>
                            <Typography variant="h6" mb={1}>Pending Uploads:</Typography>
                            {newUploads.map((upload, idx) => (
                                <Box key={idx} mb={2}>
                                    <TextField
                                        fullWidth
                                        label="Document Name"
                                        value={upload.name}
                                        onChange={(e) => {
                                            const updated = [...newUploads];
                                            updated[idx].name = e.target.value;
                                            setNewUploads(updated);
                                        }}
                                        sx={{ mb: 1 }}
                                    />
                                    <TextField
                                        select
                                        fullWidth
                                        label="Document Type"
                                        value={upload.type}
                                        onChange={(e) => {
                                            const updated = [...newUploads];
                                            updated[idx].type = e.target.value;
                                            setNewUploads(updated);
                                        }}
                                    >
                                        {docTypes.map((type) => (
                                            <MenuItem key={type} value={type}>{type}</MenuItem>
                                        ))}
                                    </TextField>
                                    <Divider sx={{ my: 2 }} />
                                </Box>
                            ))}

                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleUploadConfirm}
                                disabled={isUploading}
                            >
                                {isUploading ? 'Uploading...' : 'Confirm Upload'}
                            </Button>
                            {error && (
                                <Typography color="error.main" mt={2}>
                                    Upload failed. Please try again.
                                </Typography>
                            )}
                            {isSuccess && (
                                <Typography color="success.main" mt={2}>
                                    Upload successful!
                                </Typography>
                            )}
                        </>
                    )}
                </Paper>
            </Box>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete "{docToDelete ? (docToDelete.fileName || docToDelete._doc?.fileName) : ''}"?
                    </DialogContentText>
                    {isDeleting && (
                        <Typography variant="caption" color="text.secondary">
                            Deleting...
                        </Typography>
                    )}
                    {deleteError && (
                        <Typography variant="caption" color="error">
                            Delete failed. Please try again.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default StationUploadedDocs;
